/**
 * A resident's own named times of day, beside the home's meal times — the
 * owner's request of 4 October 2026, the same as on the phone.
 *
 * "Evening tea, 17:00": up to six per resident, the name as typed. A
 * medicine is tied to one before, with or after, as to a meal. Each action
 * saves at once (the server rebuilds the doses tied to a time whose clock
 * time changed). Deleting one that medicines are tied to asks where they go:
 * another time, or the same clock times every day. Nothing is dropped.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from '@drbell/ui';
import { api, ApiError, type RoutineTime } from '../api.ts';

const MAX = 6;
const MEALS: ReadonlyArray<readonly [string, string]> = [
  ['breakfast', 'Breakfast'], ['lunch', 'Lunch'], ['dinner', 'Dinner'], ['bedtime', 'Bedtime'],
];

export function ResidentTimes({ residentId }: { residentId: string }) {
  const [times, setTimes] = useState<RoutineTime[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { name: string; time: string }>>({});
  const [adding, setAdding] = useState<{ name: string; time: string } | null>(null);
  const [removing, setRemoving] = useState<{ time: RoutineTime; medicines: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<{ routineTimes: RoutineTime[] }>(`/api/patients/${residentId}/routine-times`);
    setTimes(r.routineTimes);
  }, [residentId]);
  useEffect(() => { void load().catch(() => setTimes([])); }, [load]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = (time: RoutineTime, removal?: { medicines: 'move'; to: string } | { medicines: 'keep' }) =>
    void run(async () => {
      try {
        await api.del(`/api/patients/${residentId}/routine-times/${time.id}`, removal);
      } catch (e) {
        if (e instanceof ApiError && e.body?.error === 'routine_time_in_use') {
          const medicines = (e.body.medicines as Array<{ name: string }> | undefined) ?? [];
          setRemoving({ time, medicines: medicines.map((m) => m.name) });
          return;
        }
        throw e;
      }
      setRemoving(null);
      toast.success(`${time.name} deleted`);
    });

  if (!times) return null;

  const input = 'min-h-11 rounded-lg border border-(--line) bg-(--panel) px-3';
  return (
    <fieldset className="mt-4 rounded-lg border border-(--line) p-3">
      <legend className="px-1 text-xs text-(--muted)">Other times of the day</legend>
      <p className="text-xs text-(--muted)">
        Times besides meals, like Evening tea. A medicine can be taken before,
        with or after one, as with a meal. Changing a time moves its reminders.
      </p>

      {times.map((t) => {
        const d = drafts[t.id] ?? { name: t.name, time: t.time };
        const changed = d.name !== t.name || d.time !== t.time;
        const set = (next: Partial<typeof d>) => setDrafts((all) => ({ ...all, [t.id]: { ...d, ...next } }));
        return (
          <div key={t.id} className="mt-3">
            <div className="flex gap-2">
              <input aria-label="Name of the time" value={d.name} maxLength={30} onChange={(e) => set({ name: e.target.value })} className={`${input} flex-1`} />
              <input aria-label={`${t.name}, time`} type="time" value={d.time} onChange={(e) => set({ time: e.target.value })} className={input} />
            </div>
            <div className="mt-2 flex justify-end gap-2">
              {changed ? (
                <>
                  <button disabled={busy} onClick={() => setDrafts(({ [t.id]: _x, ...rest }) => rest)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Cancel</button>
                  <button
                    disabled={busy}
                    onClick={() => void run(async () => {
                      await api.put(`/api/patients/${residentId}/routine-times/${t.id}`, {
                        ...(d.name !== t.name ? { name: d.name } : {}),
                        ...(d.time !== t.time ? { time: d.time } : {}),
                      });
                      setDrafts(({ [t.id]: _x, ...rest }) => rest);
                      toast.success(d.time !== t.time ? `Saved. Every reminder tied to ${d.name} has moved to match.` : 'Saved');
                    })}
                    className="min-h-11 rounded-lg bg-(--accent) px-3 text-sm font-medium text-white disabled:opacity-40"
                  >
                    Save
                  </button>
                </>
              ) : (
                <button disabled={busy} onClick={() => remove(t)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Delete</button>
              )}
            </div>

            {removing?.time.id === t.id && (
              <div role="alert" className="mt-2 rounded-lg border border-(--line) bg-(--panel) p-3 text-sm">
                <p>
                  Medicines are taken at {t.name}: {removing.medicines.join(', ')}. Move them to
                  another time, or keep them at the same clock times every day.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[...MEALS, ...times.filter((o) => o.id !== t.id).map((o) => [`time:${o.id}`, o.name] as const)].map(([key, label]) => (
                    <button key={key} disabled={busy} onClick={() => remove(t, { medicines: 'move', to: key })} className="min-h-11 rounded-lg border border-(--line) px-3">
                      Move to {label}
                    </button>
                  ))}
                  <button disabled={busy} onClick={() => remove(t, { medicines: 'keep' })} className="min-h-11 rounded-lg bg-(--accent) px-3 font-medium text-white">
                    Keep the same clock times
                  </button>
                  <button disabled={busy} onClick={() => setRemoving(null)} className="min-h-11 rounded-lg border border-(--line) px-3">Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="mt-3">
          <div className="flex gap-2">
            <input aria-label="Name of the time" placeholder="Evening tea" value={adding.name} maxLength={30} autoFocus onChange={(e) => setAdding({ ...adding, name: e.target.value })} className={`${input} flex-1`} />
            <input aria-label="Time" type="time" value={adding.time} onChange={(e) => setAdding({ ...adding, time: e.target.value })} className={input} />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button disabled={busy} onClick={() => setAdding(null)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">Cancel</button>
            <button
              disabled={busy || adding.name.trim().length === 0}
              onClick={() => void run(async () => {
                await api.post(`/api/patients/${residentId}/routine-times`, adding);
                setAdding(null);
              })}
              className="min-h-11 rounded-lg bg-(--accent) px-3 text-sm font-medium text-white disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </div>
      ) : times.length >= MAX ? (
        <p className="mt-3 text-xs text-(--muted)">Up to {MAX} other times of the day.</p>
      ) : (
        <button disabled={busy} onClick={() => setAdding({ name: '', time: '17:00' })} className="mt-3 min-h-11 rounded-lg border border-(--line) px-3 text-sm">
          + Add a time
        </button>
      )}

      {error && <p role="alert" className="mt-2 text-sm text-red-500">{error}</p>}
    </fieldset>
  );
}
